/* blank-apparel-updated.js — high-variant safe BULK selector (Storefront API)
 *
 * Stok Storefront API quantityAvailable'dan gelir (250 limiti + deferred-loading baypas).
 * Sayfa açılınca tüm varyantlar bir kez çekilip renge göre gruplanır; renk seçince o
 * rengin bedenleri cache'den anında, gerçek stoğuyla render edilir.
 *
 * Block üzerinde gereken data attr'ları:
 *   data-storefront-token (unauthenticated_read_product_inventory + _product_listings scope)
 *   data-shop, data-api-version, data-product-handle
 */
class BulkVariantSelector {
  constructor(root) {
    this.root = root;
    this.handle  = root.dataset.productHandle;
    this.shop    = root.dataset.shop;
    this.version = root.dataset.apiVersion || '2025-07';
    this.token   = root.dataset.storefrontToken;
    this.minQty  = parseInt(root.dataset.minQty, 10) || 0;
    this.vendor  = root.dataset.vendor || '';
    this.mainImageSelector = root.dataset.mainImage || '#mainProductImage';
    this.colorNames = (root.dataset.colorNames || 'Color,Renk').split(',').map(s => s.trim().toLowerCase());
    this.sizeNames  = (root.dataset.sizeNames  || 'Size,Beden').split(',').map(s => s.trim().toLowerCase());

    this.selections = {};   // variantId -> { qty, color, size, price(cents) }
    this.byColor = {};      // color -> [ { size, variantId, qty(null=unknown), price(cents), available } ]

    this.cacheRefs();
    this.bind();
    const sel = this.root.querySelector('.pvs__swatch.is-selected') || this.root.querySelector('.pvs__swatch');
    this.currentColor = sel ? sel.dataset.color : (this.sizes?.dataset.selectedColor || '');
    this.refreshUI();
    this.loadInventory();
  }

  cacheRefs() {
    this.sizes        = this.root.querySelector('.pvs__sizes');
    this.summary      = this.root.querySelector('.pvs__summary');
    this.summaryBody  = this.root.querySelector('.pvs__summary-body');
    this.totalUnitsEl = this.root.querySelector('.pvs__total-units');
    this.totalPriceEl = this.root.querySelector('.pvs__total-price');
    this.atc          = this.root.querySelector('.pvs__atc');
    this.atcLabel     = this.root.querySelector('.pvs__atc-label');
    this.warnEl       = this.root.querySelector('.pvs__min-warning');
    this.colorLabel   = this.root.querySelector('.pvs__selected-color');
  }

  bind() {
    this.root.addEventListener('click', (e) => {
      const minus = e.target.closest('.pvs__step--minus');
      if (minus) { e.preventDefault(); return this.step(minus, -1); }
      const plus = e.target.closest('.pvs__step--plus');
      if (plus) { e.preventDefault(); return this.step(plus, +1); }
      const swatch = e.target.closest('.pvs__swatch');
      if (swatch) { e.preventDefault(); return this.selectColor(swatch); }
      if (e.target.closest('.pvs__clear')) { e.preventDefault(); return this.clearAll(); }
      if (e.target.closest('.pvs__atc')) { e.preventDefault(); return this.addToCart(); }
    });
    this.root.addEventListener('input',  (e) => { if (e.target.classList.contains('pvs__qty')) this.onQty(e.target); });
    this.root.addEventListener('change', (e) => { if (e.target.classList.contains('pvs__qty')) this.onQty(e.target); });
  }

  // ---------- STOREFRONT API ----------
  async loadInventory() {
    if (!this.token) {
      this.sizes.innerHTML = '<div class="pvs__loading">Storefront token is not set (block setting).</div>';
      console.error('[BulkVariantSelector] Storefront token missing (block settings -> Storefront API token).');
      return;
    }
    const endpoint = `https://${this.shop}/api/${this.version}/graphql.json`;
    const query = `query($handle:String!,$cursor:String){
      product(handle:$handle){
        variants(first:250, after:$cursor){
          pageInfo{ hasNextPage endCursor }
          nodes{ id availableForSale quantityAvailable price{ amount } selectedOptions{ name value } }
        }
      }
    }`;
    try {
      let cursor = null, hasNext = true, guard = 0;
      const all = [];
      while (hasNext && guard < 12) {
        guard++;
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': this.token },
          body: JSON.stringify({ query, variables: { handle: this.handle, cursor } })
        });
        const json = await res.json();
        if (json.errors) { console.error('[BulkVariantSelector] Storefront errors:', json.errors); break; }
        const conn = json.data?.product?.variants;
        if (!conn) break;
        all.push(...conn.nodes);
        hasNext = conn.pageInfo.hasNextPage;
        cursor  = conn.pageInfo.endCursor;
      }
      this.buildIndex(all);
      console.info('[BulkVariantSelector] fetched variants:', all.length, '| colors:', Object.keys(this.byColor).length);
      this.renderSizes(this.currentColor);
      this.refreshUI();
    } catch (err) {
      console.error('[BulkVariantSelector] inventory fetch failed:', err);
      this.sizes.innerHTML = '<div class="pvs__loading">Could not load stock.</div>';
    }
  }

  buildIndex(nodes) {
    this.byColor = {};
    nodes.forEach(n => {
      let color = '', size = '';
      n.selectedOptions.forEach(o => {
        const name = o.name.toLowerCase();
        if (this.colorNames.includes(name)) color = o.value;
        else if (this.sizeNames.includes(name)) size = o.value;
      });
      if (!color) return;
      (this.byColor[color] = this.byColor[color] || []).push({
        size,
        variantId: n.id.split('/').pop(),
        qty: (n.quantityAvailable === null || n.quantityAvailable === undefined) ? null : n.quantityAvailable,
        price: Math.round(parseFloat(n.price.amount) * 100),
        available: n.availableForSale
      });
    });
  }

  renderSizes(color) {
    const list = this.byColor[color] || [];
    if (!this.sizes) return;
    if (list.length === 0) { this.sizes.innerHTML = '<div class="pvs__loading">No sizes for this color.</div>'; return; }
    this.sizes.innerHTML = list.map(v => {
      const known = v.qty !== null && v.qty > 0;
      const max = known ? v.qty : 9999;
      if (!v.available) {
        return `<div class="pvs__size is-out">
          <div class="pvs__size-name">${v.size}</div>
          <div class="pvs__size-price">${this.money(v.price)}</div>
          <div class="pvs__size-stock pvs__size-stock--out">OUT</div>
        </div>`;
      }
      return `<div class="pvs__size" data-variant-id="${v.variantId}" data-size="${v.size}"
                   data-color="${color}" data-price="${v.price}" data-max="${max}">
        <div class="pvs__size-name">${v.size}</div>
        <div class="pvs__size-price">${this.money(v.price)}</div>
        <div class="pvs__stepper">
          <button type="button" class="pvs__step pvs__step--minus" aria-label="decrease">&minus;</button>
          <input type="number" class="pvs__qty" value="0" min="0" max="${max}" data-variant-id="${v.variantId}" inputmode="numeric">
          <button type="button" class="pvs__step pvs__step--plus" aria-label="increase">+</button>
        </div>
        <div class="pvs__size-stock">${known ? v.qty + ' in stock' : 'In stock'}</div>
      </div>`;
    }).join('');
    this.restoreQuantities();
  }

  // ---------- COLOR / QUANTITY ----------
  selectColor(swatch) {
    this.currentColor = swatch.dataset.color;
    this.root.querySelectorAll('.pvs__swatch').forEach(s => s.classList.remove('is-selected'));
    swatch.classList.add('is-selected');
    if (this.colorLabel) this.colorLabel.textContent = swatch.dataset.color;
    this.updateMainImage(swatch.dataset.variantImage, swatch.dataset.mediaId);
    this.renderSizes(this.currentColor);
    this.refreshUI();
  }

  step(btn, dir) {
    const input = btn.closest('.pvs__stepper')?.querySelector('.pvs__qty');
    if (!input) return;
    const max = parseInt(input.max, 10);
    const cap = Number.isFinite(max) ? max : 9999;
    input.value = Math.min(Math.max((parseInt(input.value, 10) || 0) + dir, 0), cap);
    this.onQty(input);
  }

  onQty(input) {
    const max = parseInt(input.max, 10);
    const cap = Number.isFinite(max) ? max : 9999;
    const val = Math.min(Math.max(parseInt(input.value, 10) || 0, 0), cap);
    input.value = val;
    const box = input.closest('.pvs__size');
    const id  = input.dataset.variantId;
    if (val > 0) {
      this.selections[id] = { qty: val, color: box?.dataset.color || this.currentColor,
                              size: box?.dataset.size || '', price: parseInt(box?.dataset.price, 10) || 0 };
    } else delete this.selections[id];
    this.refreshUI();
  }

  restoreQuantities() {
    this.sizes?.querySelectorAll('.pvs__qty').forEach(input => {
      const saved = this.selections[input.dataset.variantId];
      if (saved) input.value = saved.qty;
    });
  }

  updateMainImage(src, mediaId) {
    if (src) {
      let img = (this.mainImageSelector && document.querySelector(this.mainImageSelector)) || null;
      if (!img || img.tagName !== 'IMG') {
        img = document.querySelector([
          '.product__media img', '.product-single__photo img', '.product__main-photos img',
          '.product-gallery__image img', '[data-product-featured-media] img',
          '.product__media-item.is-active img', '.flickity-slider .is-selected img', '.product__photo img'
        ].join(', '));
      }
      if (img && img.tagName === 'IMG') { img.src = src; img.removeAttribute('srcset'); img.removeAttribute('data-srcset'); }
    }
    if (mediaId) {
      const thumb = document.querySelector(`[data-media-id="${mediaId}"], [data-id="${mediaId}"], a[href*="${mediaId}"]`);
      if (thumb) thumb.click();
    }
  }

  // ---------- UI / SUMMARY ----------
  refreshUI() {
    const entries = Object.values(this.selections);
    const totalUnits = entries.reduce((s, e) => s + e.qty, 0);
    const totalPrice = entries.reduce((s, e) => s + e.qty * e.price, 0);

    if (this.summary) {
      if (totalUnits === 0) this.summary.hidden = true;
      else {
        this.summary.hidden = false;
        this.summaryBody.innerHTML = '';
        Object.values(this.selections).forEach(e => {
          const row = document.createElement('tr');
          row.innerHTML = `<td>${e.color}</td><td>${e.size}</td><td>${e.qty}</td>` +
                          `<td>${this.money(e.price)}</td><td>${this.money(e.price * e.qty)}</td>`;
          this.summaryBody.appendChild(row);
        });
        if (this.totalUnitsEl) this.totalUnitsEl.textContent = `${totalUnits} units`;
        if (this.totalPriceEl) this.totalPriceEl.textContent = this.money(totalPrice);
      }
    }

    if (this.atc) {
      const meetsMin = this.minQty > 0 ? totalUnits >= this.minQty : totalUnits > 0;
      this.atc.disabled = !meetsMin;
      if (totalUnits === 0) this.atcLabel.textContent = 'Select sizes & quantities';
      else if (this.minQty > 0 && totalUnits < this.minQty)
        this.atcLabel.textContent = `Add ${this.minQty - totalUnits} more to reach minimum`;
      else this.atcLabel.textContent = `Add ${totalUnits} item${totalUnits > 1 ? 's' : ''} to cart`;
    }

    if (this.warnEl) {
      const show = this.minQty > 0 && totalUnits > 0 && totalUnits < this.minQty;
      this.warnEl.hidden = !show;
      if (show) this.warnEl.textContent = `Minimum order quantity is ${this.minQty}.`;
    }
  }

  clearAll() {
    this.selections = {};
    this.sizes?.querySelectorAll('.pvs__qty').forEach(i => (i.value = 0));
    this.refreshUI();
  }

  // ---------- BULK ADD TO CART ----------
  // Sarmalanmamış fetch: gizli bir iframe'in contentWindow.fetch'i monkey-patch'lenmemiştir.
  getCleanFetch() {
    if (this._cleanFetch) return this._cleanFetch;
    try {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.setAttribute('aria-hidden', 'true');
      (document.body || document.documentElement).appendChild(iframe);
      const f = iframe.contentWindow.fetch;
      if (typeof f !== 'function') throw new Error('no iframe fetch');
      this._cleanIframe = iframe; // iframe'i bırakıyoruz; kaldırırsak fetch geçersiz olur
      this._cleanFetch = (...args) => f.apply(iframe.contentWindow, args);
    } catch (e) {
      console.warn('[BulkVariantSelector] clean fetch alınamadı, window.fetch kullanılacak:', e);
      this._cleanFetch = window.fetch.bind(window);
    }
    return this._cleanFetch;
  }

  async addToCart() {
    const items = Object.entries(this.selections)
      .filter(([, e]) => e.qty > 0)
      .map(([id, e]) => {
        const item = { id: parseInt(id, 10), quantity: e.qty };
        if (this.vendor) item.properties = { 'Brand': this.vendor };
        return item;
      });
    if (items.length === 0) { alert('Please select at least one size and quantity'); return; }
    if (this.minQty > 0 && items.reduce((s, i) => s + i.quantity, 0) < this.minQty) {
      if (this.warnEl) this.warnEl.hidden = false; return;
    }
    this.atc.disabled = true;
    const prev = this.atcLabel.textContent;
    this.atcLabel.textContent = 'Adding…';
    try {
      // Mağazanın/app'lerin window.fetch sarmalayıcıları /cart/add.js'i ele geçirip
      // varyant ID'sini native formdaki (varsayılan) varyantla değiştiriyor. Temiz,
      // dokunulmamış bir fetch ile bunu baypas ediyoruz. Iframe fetch'i göreli URL'i
      // about:blank'e göre çözer; bu yüzden MUTLAK URL kullanıyoruz.
      const doFetch = this.getCleanFetch();
      const res = await doFetch(`${window.location.origin}/cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Shopify hata gövdesi: { status, message, description }
        console.error('[BulkVariantSelector] cart add rejected:', res.status, data);
        const msg = data.description || data.message || `Could not add to cart (HTTP ${res.status}).`;
        alert(msg);
        this.atcLabel.textContent = prev;
        this.atc.disabled = false;
        return;
      }
      if (typeof window.openCartDrawer === 'function') window.openCartDrawer();
      else if (typeof window.refreshCart === 'function') window.refreshCart();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      this.clearAll();
    } catch (err) {
      console.error('[BulkVariantSelector] cart add failed:', err);
      alert('Failed to add to cart. Please try again.');
      this.atcLabel.textContent = prev;
      this.atc.disabled = false;
    }
  }

  money(cents) {
    const fmt = window.theme?.moneyFormat;
    if (fmt) return fmt.replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/, (cents / 100).toFixed(2));
    return `$${(cents / 100).toFixed(2)}`;
  }
}

(function init() {
  const run = () => document.querySelectorAll('.pvs').forEach(root => {
    if (!root.__pvsInit) { root.__pvsInit = true; new BulkVariantSelector(root); }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();